import {
  CanvasTexture,
  LinearFilter,
  LinearMipMapLinearFilter,
  RepeatWrapping,
  SRGBColorSpace,
  type Texture,
} from "three";

/**
 * Catalog/API does not expose dedicated room-floor materials.
 * Curated presets map to the existing PlanarGraph3DView floor material path:
 * - wood (legacy) → CDN parquet texture (2145.jpg) + color tint
 * - wood (mapUrl) → CDN/local albedo image + RepeatWrapping
 * - wood (patterned) / marble / tile / concrete → procedural CanvasTexture + PBR params
 */

export const LEGACY_FLOOR_TEXTURE_URL =
  "https://s3.eu-central-1.amazonaws.com/cdn.sugartech/mottobucket/CDN/sugar-viewer/2145.jpg";

/** Image albedo for photographic wood presets (publish uploads here). */
export const FLOOR_IMAGE_CDN_BASE =
  "https://s3.eu-central-1.amazonaws.com/cdn.sugartech/mottobucket/CDN/sugar-planner/floors";

/** Local public/ for dev; CDN for published embeds. */
export function floorImageUrl(fileName: string): string {
  if (
    typeof location !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/.test(location.hostname)
  ) {
    return `/assets/floors/${fileName}`;
  }
  return `${FLOOR_IMAGE_CDN_BASE}/${fileName}`;
}

export type FloorTextureKind =
  | "wood"
  | "marble"
  | "tile"
  | "concrete"
  | "grass";

/** Wood map source: legacy CDN tint, or a procedural plank pattern. */
export type WoodFloorPattern = "legacy" | "herringbone" | "wide-plank" | "strip";

/** i18n key for floor preset labels (tr/en/de). */
export type FloorMaterialLabelKey = string;

/** Photographic plank series physical size (mm → m). */
export const PARQUET_PLANK_LENGTH_M = 1.195;
export const PARQUET_PLANK_WIDTH_M = 0.189;

/**
 * UV repeat tuned for 1195×189 mm plank textures on room-floor UVs.
 * (~room meters / plank length ≈ 1.6–2 for typical rooms)
 */
const PARQUET_1195_MAP_REPEAT = 1.7;

function buildParquet1195Series(): FloorMaterialPreset[] {
  const out: FloorMaterialPreset[] = [];
  for (let i = 1; i <= 22; i++) {
    const n = String(i).padStart(2, "0");
    const file = `parquet-1195x189-${n}.png`;
    const url = floorImageUrl(file);
    out.push({
      id: `wood-parquet-1195-${n}`,
      labelKey: `floorMatParquet${n}`,
      kind: "wood",
      color: 0xe8e0d4,
      roughness: 0.88,
      metalness: 0,
      mapUrl: url,
      mapRepeat: PARQUET_1195_MAP_REPEAT,
      thumbnail: `url("${url}")`,
    });
  }
  return out;
}

export interface FloorMaterialPreset {
  id: string;
  labelKey: FloorMaterialLabelKey;
  kind: FloorTextureKind;
  /** Applied via room surface floor color. */
  color: number;
  roughness: number;
  metalness: number;
  /** CSS background for UI thumbnail. */
  thumbnail: string;
  /** Wood-only: defaults to legacy CDN parquet when omitted. */
  woodPattern?: WoodFloorPattern;
  /** Image albedo URL (local/static or remote). Takes precedence over woodPattern. */
  mapUrl?: string;
  /** Marble-only: vein accent color (defaults from base). */
  veinAccent?: number;
  /** UV repeat for procedural / image maps. */
  mapRepeat?: number;
}

export const DEFAULT_FLOOR_MATERIAL_ID = "wood-oak-parquet";

const LEGACY_FLOOR_MATERIAL_PRESETS: readonly FloorMaterialPreset[] = [
  {
    id: "wood-oak-parquet",
    labelKey: "floorMatOakParquet",
    kind: "wood",
    color: 0xf0e0bc,
    roughness: 0.88,
    metalness: 0,
    thumbnail:
      "linear-gradient(135deg, #e8d4a8 0%, #d4b88a 35%, #c4a06e 55%, #d8bc96 75%, #e2cba8 100%)",
  },
  {
    id: "wood-walnut-parquet",
    labelKey: "floorMatWalnutParquet",
    kind: "wood",
    color: 0xb8956c,
    roughness: 0.9,
    metalness: 0,
    thumbnail:
      "linear-gradient(135deg, #8b6914 0%, #6b4423 30%, #5c3a1e 50%, #7a5230 72%, #4a2c14 100%)",
  },
  {
    id: "wood-ash-parquet",
    labelKey: "floorMatAshParquet",
    kind: "wood",
    color: 0xe8e4df,
    roughness: 0.86,
    metalness: 0,
    thumbnail:
      "linear-gradient(135deg, #f2efe8 0%, #ddd6cc 40%, #cfc6ba 60%, #e8e2d8 100%)",
  },
  {
    id: "wood-honey-oak",
    labelKey: "floorMatHoneyOak",
    kind: "wood",
    color: 0xd8c2a6,
    roughness: 0.87,
    metalness: 0,
    thumbnail:
      "linear-gradient(135deg, #e8c48a 0%, #d4a86a 35%, #c99650 55%, #e0b878 80%, #c48a40 100%)",
  },
  {
    id: "wood-dark-oak",
    labelKey: "floorMatDarkOak",
    kind: "wood",
    color: 0x8b7355,
    roughness: 0.92,
    metalness: 0,
    thumbnail:
      "linear-gradient(135deg, #5c4030 0%, #3e2723 40%, #4e342e 60%, #2c1a12 100%)",
  },
  {
    id: "wood-cherry-parquet",
    labelKey: "floorMatCherryParquet",
    kind: "wood",
    color: 0xc4866a,
    roughness: 0.88,
    metalness: 0,
    woodPattern: "strip",
    mapRepeat: 2,
    thumbnail:
      "repeating-linear-gradient(90deg, #c4866a 0 10px, #a86850 10px 12px, #d49878 12px 22px, #8f523c 22px 24px)",
  },
  {
    id: "wood-teak-parquet",
    labelKey: "floorMatTeakParquet",
    kind: "wood",
    color: 0xc9a66b,
    roughness: 0.86,
    metalness: 0,
    woodPattern: "strip",
    mapRepeat: 2,
    thumbnail:
      "repeating-linear-gradient(90deg, #c9a66b 0 10px, #a88448 10px 12px, #dbb87c 12px 22px, #8a6a38 22px 24px)",
  },
  {
    id: "wood-smoked-oak",
    labelKey: "floorMatSmokedOak",
    kind: "wood",
    color: 0x6e6458,
    roughness: 0.93,
    metalness: 0,
    woodPattern: "strip",
    mapRepeat: 2,
    thumbnail:
      "repeating-linear-gradient(90deg, #6e6458 0 10px, #4a443c 10px 12px, #7a7064 12px 22px, #3a342e 22px 24px)",
  },
  {
    id: "wood-oak-herringbone",
    labelKey: "floorMatOakHerringbone",
    kind: "wood",
    color: 0xe6d2a8,
    roughness: 0.88,
    metalness: 0,
    woodPattern: "herringbone",
    mapRepeat: 2.5,
    thumbnail:
      "repeating-linear-gradient(45deg, #e6d2a8 0 8px, #c4a878 8px 10px, #d8bc90 10px 18px), repeating-linear-gradient(-45deg, #dcc498 0 8px, #b89868 8px 10px, transparent 10px 18px)",
  },
  {
    id: "wood-walnut-herringbone",
    labelKey: "floorMatWalnutHerringbone",
    kind: "wood",
    color: 0x8a6238,
    roughness: 0.9,
    metalness: 0,
    woodPattern: "herringbone",
    mapRepeat: 2.5,
    thumbnail:
      "repeating-linear-gradient(45deg, #8a6238 0 8px, #5c3a1e 8px 10px, #7a5230 10px 18px), repeating-linear-gradient(-45deg, #6b4423 0 8px, #3e2410 8px 10px, transparent 10px 18px)",
  },
  {
    id: "wood-oak-wide-plank",
    labelKey: "floorMatOakWidePlank",
    kind: "wood",
    color: 0xe8d4b0,
    roughness: 0.87,
    metalness: 0,
    woodPattern: "wide-plank",
    mapRepeat: 1.25,
    thumbnail:
      "repeating-linear-gradient(0deg, #e8d4b0 0 22px, #c4a878 22px 24px, #dcc498 24px 46px, #b89868 46px 48px)",
  },
  {
    id: "wood-walnut-wide-plank",
    labelKey: "floorMatWalnutWidePlank",
    kind: "wood",
    color: 0x7a5230,
    roughness: 0.9,
    metalness: 0,
    woodPattern: "wide-plank",
    mapRepeat: 1.25,
    thumbnail:
      "repeating-linear-gradient(0deg, #7a5230 0 22px, #4a2c14 22px 24px, #6b4423 24px 46px, #3e2410 46px 48px)",
  },
  {
    id: "wood-ash-washed-plank",
    labelKey: "floorMatAshWashedPlank",
    kind: "wood",
    color: 0xb8b2a6,
    roughness: 0.88,
    metalness: 0,
    mapUrl: floorImageUrl("parquet-ash-plank.png"),
    mapRepeat: 3,
    thumbnail: `url("${floorImageUrl("parquet-ash-plank.png")}")`,
  },
  {
    id: "wood-oak-chevron",
    labelKey: "floorMatOakChevron",
    kind: "wood",
    color: 0xd8c4a0,
    roughness: 0.86,
    metalness: 0,
    mapUrl: floorImageUrl("parquet-oak-chevron.png"),
    mapRepeat: 2.5,
    thumbnail: `url("${floorImageUrl("parquet-oak-chevron.png")}")`,
  },
  {
    id: "wood-bleached-oak",
    labelKey: "floorMatBleachedOak",
    kind: "wood",
    color: 0xe8e4dc,
    roughness: 0.87,
    metalness: 0,
    mapUrl: floorImageUrl("floor-bleached-oak.png"),
    mapRepeat: 2.5,
    thumbnail: `url("${floorImageUrl("floor-bleached-oak.png")}")`,
  },
  {
    id: "wood-silver-washed-oak",
    labelKey: "floorMatSilverWashedOak",
    kind: "wood",
    color: 0xd8d4cc,
    roughness: 0.88,
    metalness: 0,
    mapUrl: floorImageUrl("floor-silver-washed-oak.png"),
    mapRepeat: 2.5,
    thumbnail: `url("${floorImageUrl("floor-silver-washed-oak.png")}")`,
  },
  {
    id: "wood-warm-oak",
    labelKey: "floorMatWarmOak",
    kind: "wood",
    color: 0xd4b896,
    roughness: 0.86,
    metalness: 0,
    mapUrl: floorImageUrl("floor-warm-oak.png"),
    mapRepeat: 2.5,
    thumbnail: `url("${floorImageUrl("floor-warm-oak.png")}")`,
  },
  {
    id: "tile-beige-geo-panel",
    labelKey: "floorMatBeigeGeoPanel",
    kind: "tile",
    color: 0xe8e0d4,
    roughness: 0.72,
    metalness: 0.02,
    mapUrl: floorImageUrl("floor-beige-geo-panel.png"),
    mapRepeat: 2,
    thumbnail: `url("${floorImageUrl("floor-beige-geo-panel.png")}")`,
  },
  {
    id: "tile-charcoal-geo-panel",
    labelKey: "floorMatCharcoalGeoPanel",
    kind: "tile",
    color: 0x6a6a68,
    roughness: 0.7,
    metalness: 0.04,
    mapUrl: floorImageUrl("floor-charcoal-geo-panel.png"),
    mapRepeat: 2,
    thumbnail: `url("${floorImageUrl("floor-charcoal-geo-panel.png")}")`,
  },
  {
    id: "marble-travertine-beige",
    labelKey: "floorMatTravertineBeige",
    kind: "marble",
    color: 0xe6dcc8,
    roughness: 0.55,
    metalness: 0.02,
    mapUrl: floorImageUrl("floor-travertine-beige.png"),
    mapRepeat: 2.2,
    thumbnail: `url("${floorImageUrl("floor-travertine-beige.png")}")`,
  },
  {
    id: "marble-white",
    labelKey: "floorMatWhiteMarble",
    kind: "marble",
    color: 0xf5f1e8,
    roughness: 0.28,
    metalness: 0.05,
    thumbnail:
      "linear-gradient(122deg, #faf8f5 0%, #ece8e1 28%, #d8d2c8 42%, #f7f4ef 58%, #cfc8bc 78%, #faf8f5 100%)",
  },
  {
    id: "marble-carrara",
    labelKey: "floorMatCarraraMarble",
    kind: "marble",
    color: 0xf2efe6,
    roughness: 0.32,
    metalness: 0.04,
    thumbnail:
      "linear-gradient(128deg, #f4f2ee 0%, #ddd9d2 25%, #bdb8ae 38%, #eeebe6 55%, #9a958c 72%, #f0ede8 100%)",
  },
  {
    id: "marble-gray",
    labelKey: "floorMatGrayMarble",
    kind: "marble",
    color: 0xd6d6d6,
    roughness: 0.35,
    metalness: 0.06,
    thumbnail:
      "linear-gradient(125deg, #e0e0e0 0%, #b8b8b8 30%, #8e8e8e 48%, #cfcfcf 68%, #6e6e6e 88%, #d8d8d8 100%)",
  },
  {
    id: "marble-black",
    labelKey: "floorMatBlackMarble",
    kind: "marble",
    color: 0x5a5a58,
    roughness: 0.3,
    metalness: 0.08,
    thumbnail:
      "linear-gradient(130deg, #3a3a38 0%, #1e1e1c 35%, #4a4a46 50%, #2a2a28 70%, #5c5c58 100%)",
  },
  {
    id: "marble-calacatta",
    labelKey: "floorMatCalacattaMarble",
    kind: "marble",
    color: 0xf7f1e4,
    roughness: 0.26,
    metalness: 0.06,
    veinAccent: 0xb8965a,
    thumbnail:
      "linear-gradient(118deg, #faf6ee 0%, #efe6d4 22%, #c9a86a 38%, #f5efe4 55%, #a88448 72%, #faf6ee 100%)",
  },
  {
    id: "marble-verde",
    labelKey: "floorMatVerdeMarble",
    kind: "marble",
    color: 0x6a8a72,
    roughness: 0.34,
    metalness: 0.05,
    veinAccent: 0x2e4a36,
    thumbnail:
      "linear-gradient(126deg, #8aaa92 0%, #6a8a72 25%, #3e6048 42%, #7a9a82 60%, #2a4432 80%, #6a8a72 100%)",
  },
  {
    id: "marble-travertine",
    labelKey: "floorMatTravertineMarble",
    kind: "marble",
    color: 0xe8dcc8,
    roughness: 0.48,
    metalness: 0.02,
    veinAccent: 0xc4b090,
    thumbnail:
      "linear-gradient(180deg, #efe6d4 0%, #e0d2b8 30%, #cfc0a4 50%, #e8dcc8 70%, #b8a888 100%)",
  },
  {
    id: "marble-rosso",
    labelKey: "floorMatRossoMarble",
    kind: "marble",
    color: 0x8a4a48,
    roughness: 0.33,
    metalness: 0.06,
    veinAccent: 0xd8b8a8,
    thumbnail:
      "linear-gradient(124deg, #a85854 0%, #7a3c3a 28%, #d8b8a8 45%, #6a3230 65%, #9a5854 100%)",
  },
  {
    id: "tile-light",
    labelKey: "floorMatLightTile",
    kind: "tile",
    color: 0xe8e4df,
    roughness: 0.55,
    metalness: 0,
    thumbnail:
      "repeating-linear-gradient(0deg, #d8d4ce 0 2px, transparent 2px 28px), repeating-linear-gradient(90deg, #d8d4ce 0 2px, #f0ece6 2px 28px)",
  },
  {
    id: "tile-gray",
    labelKey: "floorMatGrayTile",
    kind: "tile",
    color: 0xd9d4c7,
    roughness: 0.58,
    metalness: 0,
    thumbnail:
      "repeating-linear-gradient(0deg, #a8a49a 0 2px, transparent 2px 28px), repeating-linear-gradient(90deg, #a8a49a 0 2px, #c8c4b8 2px 28px)",
  },
  {
    id: "tile-terracotta",
    labelKey: "floorMatTerracottaTile",
    kind: "tile",
    color: 0xc4a484,
    roughness: 0.62,
    metalness: 0,
    thumbnail:
      "repeating-linear-gradient(0deg, #8a6048 0 2px, transparent 2px 28px), repeating-linear-gradient(90deg, #8a6048 0 2px, #c48860 2px 28px)",
  },
  {
    id: "concrete-soft",
    labelKey: "floorMatConcrete",
    kind: "concrete",
    color: 0xd6d6d6,
    roughness: 0.95,
    metalness: 0,
    thumbnail:
      "linear-gradient(160deg, #c8c8c6 0%, #b0b0ae 40%, #9a9a98 70%, #bcbcba 100%)",
  },
  {
    id: "grass-lawn",
    labelKey: "floorMatGrass",
    kind: "grass",
    color: 0x6f9b4c,
    roughness: 0.98,
    metalness: 0,
    mapRepeat: 3,
    thumbnail:
      "radial-gradient(circle at 20% 30%, #8fbf5a 0%, transparent 40%), radial-gradient(circle at 70% 60%, #5a8a38 0%, transparent 45%), linear-gradient(145deg, #7eb050 0%, #5f9140 40%, #4a7a32 70%, #6fa048 100%)",
  },
];

/** Legacy catalog with 1195×189 mm photographic parkets after Ceviz geniş lama. */
export const FLOOR_MATERIAL_PRESETS: readonly FloorMaterialPreset[] = (() => {
  const parquet1195 = buildParquet1195Series();
  const insertAfterId = "wood-walnut-wide-plank";
  const idx = LEGACY_FLOOR_MATERIAL_PRESETS.findIndex(
    (preset) => preset.id === insertAfterId,
  );
  if (idx < 0) {
    return [...LEGACY_FLOOR_MATERIAL_PRESETS, ...parquet1195];
  }
  return [
    ...LEGACY_FLOOR_MATERIAL_PRESETS.slice(0, idx + 1),
    ...parquet1195,
    ...LEGACY_FLOOR_MATERIAL_PRESETS.slice(idx + 1),
  ];
})();

const PRESET_BY_ID = new Map(
  FLOOR_MATERIAL_PRESETS.map((preset) => [preset.id, preset]),
);

export function floorMaterialById(
  id: string | undefined | null,
): FloorMaterialPreset {
  if (id && PRESET_BY_ID.has(id)) return PRESET_BY_ID.get(id)!;
  return PRESET_BY_ID.get(DEFAULT_FLOOR_MATERIAL_ID)!;
}

/** True when albedo comes from a static/remote image URL. */
export function floorUsesImageMap(preset: FloorMaterialPreset): boolean {
  return !!preset.mapUrl;
}

/** True when albedo comes from a CanvasTexture (not the legacy CDN wood map). */
export function floorUsesProceduralMap(preset: FloorMaterialPreset): boolean {
  if (floorUsesImageMap(preset)) return false;
  if (preset.kind !== "wood") return true;
  return !!preset.woodPattern && preset.woodPattern !== "legacy";
}

/** True when multiply tint should stay white (map already carries color). */
export function floorKeepsNaturalAlbedo(preset: FloorMaterialPreset): boolean {
  return floorUsesImageMap(preset) || floorUsesProceduralMap(preset);
}

function hexToRgb(hex: number): [number, number, number] {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
}

function configureMap(texture: Texture, repeat = 1): Texture {
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.minFilter = LinearMipMapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function shadeRgb(
  r: number,
  g: number,
  b: number,
  delta: number,
): [number, number, number] {
  return [
    Math.min(255, Math.max(0, r + delta)),
    Math.min(255, Math.max(0, g + delta)),
    Math.min(255, Math.max(0, b + delta)),
  ];
}

function drawWoodGrain(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  angle: number,
  grain: string,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate(angle);
  ctx.strokeStyle = grain;
  ctx.lineWidth = 1;
  const span = Math.max(w, h) * 1.6;
  for (let i = -span; i < span; i += 3 + Math.random() * 4) {
    ctx.beginPath();
    ctx.moveTo(i, -span);
    ctx.lineTo(i + (Math.random() - 0.5) * 2, span);
    ctx.globalAlpha = 0.08 + Math.random() * 0.1;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function makeHerringboneTexture(
  baseHex: number,
  repeat: number,
): CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  const [gr, gg, gb] = shadeRgb(br, bg, bb, -55);
  ctx.fillStyle = `rgb(${gr},${gg},${gb})`;
  ctx.fillRect(0, 0, size, size);

  const plankW = 26;
  const plankH = 72;
  const gap = 1.5;
  const stepX = (plankW + gap) * Math.SQRT1_2 * 2;
  const stepY = (plankH + gap) * 0.5;

  for (let col = -4; col < size / stepX + 4; col++) {
    const angle = col % 2 === 0 ? Math.PI / 4 : -Math.PI / 4;
    for (let row = -4; row < size / stepY + 4; row++) {
      const cx = col * stepX;
      const cy = row * stepY + (col % 2 === 0 ? 0 : stepY * 0.5);
      const shade = (Math.random() - 0.5) * 30;
      const [r, g, b] = shadeRgb(br, bg, bb, shade);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(-plankW / 2, -plankH / 2, plankW - gap, plankH - gap);
      drawWoodGrain(
        ctx,
        -plankW / 2,
        -plankH / 2,
        plankW - gap,
        plankH - gap,
        0,
        `rgb(${Math.max(0, r - 40)},${Math.max(0, g - 40)},${Math.max(0, b - 35)})`,
      );
      ctx.restore();
    }
  }

  return configureMap(new CanvasTexture(canvas), repeat) as CanvasTexture;
}

function makePlankTexture(
  baseHex: number,
  plankHeight: number,
  repeat: number,
): CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  const [gr, gg, gb] = shadeRgb(br, bg, bb, -50);
  ctx.fillStyle = `rgb(${gr},${gg},${gb})`;
  ctx.fillRect(0, 0, size, size);

  const gap = 2;
  let y = 0;
  let row = 0;
  while (y < size) {
    const h = plankHeight + (Math.random() - 0.5) * 6;
    const offset = row % 2 === 0 ? 0 : plankHeight * 1.6;
    let x = -offset;
    while (x < size) {
      const w = plankHeight * (2.8 + Math.random() * 1.4);
      const shade = (Math.random() - 0.5) * 26;
      const [r, g, b] = shadeRgb(br, bg, bb, shade);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(x + gap, y + gap, w - gap * 2, h - gap * 2);
      drawWoodGrain(
        ctx,
        x + gap,
        y + gap,
        w - gap * 2,
        h - gap * 2,
        0,
        `rgb(${Math.max(0, r - 35)},${Math.max(0, g - 35)},${Math.max(0, b - 30)})`,
      );
      x += w;
    }
    y += h;
    row++;
  }

  return configureMap(new CanvasTexture(canvas), repeat) as CanvasTexture;
}

function makeWoodPatternTexture(preset: FloorMaterialPreset): CanvasTexture {
  const repeat = preset.mapRepeat ?? 2;
  switch (preset.woodPattern) {
    case "herringbone":
      return makeHerringboneTexture(preset.color, repeat);
    case "wide-plank":
      return makePlankTexture(preset.color, 72, repeat);
    case "strip":
      return makePlankTexture(preset.color, 28, repeat);
    default:
      return makePlankTexture(preset.color, 40, repeat);
  }
}

function makeMarbleTexture(
  baseHex: number,
  veinAccent?: number,
): CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  ctx.fillStyle = `rgb(${br},${bg},${bb})`;
  ctx.fillRect(0, 0, size, size);

  let veinDark: string;
  let veinLight: string;
  if (veinAccent != null) {
    const [vr, vg, vb] = hexToRgb(veinAccent);
    veinDark = `rgba(${vr},${vg},${vb},0.42)`;
    veinLight = `rgba(${Math.min(255, vr + 50)},${Math.min(255, vg + 45)},${Math.min(255, vb + 40)},0.28)`;
  } else {
    veinDark = `rgba(${Math.max(0, br - 70)},${Math.max(0, bg - 70)},${Math.max(0, bb - 65)},0.35)`;
    veinLight = `rgba(${Math.min(255, br + 40)},${Math.min(255, bg + 40)},${Math.min(255, bb + 35)},0.28)`;
  }

  for (let i = 0; i < 18; i++) {
    ctx.beginPath();
    const x0 = Math.random() * size;
    const y0 = Math.random() * size;
    ctx.moveTo(x0, y0);
    for (let j = 0; j < 5; j++) {
      ctx.quadraticCurveTo(
        x0 + (Math.random() - 0.5) * 180,
        y0 + (Math.random() - 0.5) * 180,
        x0 + (Math.random() - 0.5) * 260,
        y0 + (Math.random() - 0.5) * 260,
      );
    }
    ctx.strokeStyle = i % 2 === 0 ? veinDark : veinLight;
    ctx.lineWidth = 1 + Math.random() * 3;
    ctx.stroke();
  }

  // Soft noise mottling
  const image = ctx.getImageData(0, 0, size, size);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * 16;
    data[i] = Math.min(255, Math.max(0, data[i] + n));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + n));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + n));
  }
  ctx.putImageData(image, 0, 0);

  return configureMap(new CanvasTexture(canvas)) as CanvasTexture;
}

function makeTileTexture(baseHex: number): CanvasTexture {
  const size = 512;
  const tiles = 8;
  const cell = size / tiles;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  const grout = `rgb(${Math.max(0, br - 45)},${Math.max(0, bg - 45)},${Math.max(0, bb - 40)})`;

  ctx.fillStyle = grout;
  ctx.fillRect(0, 0, size, size);

  const inset = 3;
  for (let y = 0; y < tiles; y++) {
    for (let x = 0; x < tiles; x++) {
      const shade = (Math.random() - 0.5) * 18;
      const r = Math.min(255, Math.max(0, br + shade));
      const g = Math.min(255, Math.max(0, bg + shade));
      const b = Math.min(255, Math.max(0, bb + shade));
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(
        x * cell + inset,
        y * cell + inset,
        cell - inset * 2,
        cell - inset * 2,
      );
    }
  }

  return configureMap(new CanvasTexture(canvas)) as CanvasTexture;
}

function makeConcreteTexture(baseHex: number): CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  ctx.fillStyle = `rgb(${br},${bg},${bb})`;
  ctx.fillRect(0, 0, size, size);

  const image = ctx.getImageData(0, 0, size, size);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * 28;
    data[i] = Math.min(255, Math.max(0, data[i] + n));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + n));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + n));
  }
  ctx.putImageData(image, 0, 0);

  return configureMap(new CanvasTexture(canvas)) as CanvasTexture;
}

function makeGrassTexture(baseHex: number, repeat: number): CanvasTexture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const [br, bg, bb] = hexToRgb(baseHex);
  ctx.fillStyle = `rgb(${br},${bg},${bb})`;
  ctx.fillRect(0, 0, size, size);

  // Soft turf clumps
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 8 + Math.random() * 28;
    const shade = (Math.random() - 0.5) * 50;
    const [cr, cg, cb] = shadeRgb(br, bg, bb, shade);
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(${cr},${cg},${cb},0.55)`);
    grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Fine blade strokes
  for (let i = 0; i < 1400; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const len = 3 + Math.random() * 7;
    const shade = (Math.random() - 0.5) * 40;
    const [lr, lg, lb] = shadeRgb(br, bg, bb, shade);
    ctx.strokeStyle = `rgba(${lr},${lg},${lb},0.35)`;
    ctx.lineWidth = 0.8 + Math.random();
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + (Math.random() - 0.5) * 3, y - len);
    ctx.stroke();
  }

  return configureMap(new CanvasTexture(canvas), repeat) as CanvasTexture;
}

/** Builds (or returns cached) albedo map for a procedural preset. */
export function createFloorAlbedoTexture(
  preset: FloorMaterialPreset,
): Texture | null {
  if (preset.kind === "wood") {
    if (!floorUsesProceduralMap(preset)) return null;
    return makeWoodPatternTexture(preset);
  }
  if (preset.kind === "marble") {
    return makeMarbleTexture(preset.color, preset.veinAccent);
  }
  if (preset.kind === "tile") return makeTileTexture(preset.color);
  if (preset.kind === "grass") {
    return makeGrassTexture(preset.color, preset.mapRepeat ?? 3);
  }
  return makeConcreteTexture(preset.color);
}
